import { Bell, Check, ExternalLink } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useNotifications } from "@/hooks/useNotifications";
import { formatDistanceToNow } from "date-fns";
import { el } from "date-fns/locale";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

interface NotificationsDropdownProps {
  userId: string | undefined;
}

export const NotificationsDropdown = ({ userId }: NotificationsDropdownProps) => {
  const { unreadCount, markAsRead, getRecentNotifications } = useNotifications(userId);
  const recentNotifications = getRecentNotifications(5);
  const navigate = useNavigate();

  const getNotificationIcon = (type: string) => {
    const icons: Record<string, string> = {
      new_proposal: "💼",
      proposal_accepted: "✅",
      new_job: "🔨",
      job_completed: "🎉",
      new_message: "💬",
      new_review: "⭐",
      job_cancelled: "❌",
      proposal_cancelled: "🚫",
    };
    return icons[type] || "🔔";
  };

  const handleNotificationClick = async (notification: any) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-lg">Ειδοποιήσεις</h3>
          {unreadCount > 0 && (
            <Badge variant="secondary">{unreadCount} νέες</Badge>
          )}
        </div>
        <ScrollArea className="h-[400px]">
          {recentNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Bell className="h-12 w-12 mb-2 opacity-50" />
              <p>Δεν έχετε ειδοποιήσεις</p>
            </div>
          ) : (
            <div className="divide-y">
              {recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-accent/50 cursor-pointer transition-colors ${
                    !notification.read ? "bg-accent/20" : ""
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3">
                    {notification.sender_avatar_url ? (
                      <Avatar className="h-10 w-10 flex-shrink-0">
                        <AvatarImage src={notification.sender_avatar_url} alt={notification.sender_name || "User"} />
                        <AvatarFallback>
                          {notification.sender_name?.substring(0, 2).toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                    ) : (
                      <div className="text-2xl mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                    )}
                    <div className="flex-1 space-y-1">
                      {notification.sender_name && (
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <p className="font-semibold text-sm">{notification.sender_name}</p>
                          {notification.sender_specialty && (
                            <>
                              <span className="text-muted-foreground text-xs">•</span>
                              <p className="text-xs text-muted-foreground">{notification.sender_specialty}</p>
                            </>
                          )}
                        </div>
                      )}
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-sm leading-tight">
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(notification.created_at), {
                          addSuffix: true,
                          locale: el,
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        <Separator />
        <div className="p-2">
          <Link to="/notifications">
            <Button variant="ghost" className="w-full justify-center" size="sm">
              Προβολή όλων
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
};
